import React, { useContext, useEffect } from 'react'
import { Route, Switch, useLocation, useHistory, matchPath, useRouteMatch } from 'react-router'
import type { Location } from 'history'
import { CustomMetadataProvider } from '../../providers/CustomMetadataProvider'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'
import DownloadsPage from '../Downloads'
import { BaseLayoutV2 } from '../../layouts/Base/BaseLayout'
import FileDetailModal from '../../layouts/MediaLibrary/FileDetailModal'
import { MediaLibraryProviderBridge } from '../../layouts/MediaLibrary/MediaLibraryProviderBridge'
import { MediaLibraryPage, GenerateImagesPage } from '.'

// Collections bits pulled in here
import { FeaturesEnum } from '../../../utils/features'
import CustomPlanCTA from '../../components/CustomPlan'
import { BaseLayout } from '../../layouts'
import { UserContext } from '../../providers'
import { MediaCollectionsPage } from '.'

interface BackgroundState {
  background?: Location
}

const DETAIL_PATHS = [
  '/media-library/detail/:fileId/:versionId',
  '/media-library/detail/:fileId',
  '/media-collections/:mediaCollectionId/detail/:fileId/:versionId',
  '/media-collections/:mediaCollectionId/detail/:fileId',
]

const BaseLayoutShell = ({
  children,
}: {
  children: React.ReactElement
}): React.ReactElement | null => {
  // Global "/" key shortcut to open search autocompleter
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle "/" key without modifiers
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) {
        return
      }

      // Don't interfere with form controls, text inputs, etc.
      const target = e.target as Element
      const isFormControl = target.closest(
        'input, select, textarea, [role="combobox"], [role="listbox"], [role="dialog"], [role="menu"], [role="button"], [contenteditable="true"]',
      )

      if (!isFormControl && !e.defaultPrevented) {
        e.preventDefault()
        window.dispatchEvent(new CustomEvent('mlSearchAutocomplete:open'))
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  return <BaseLayoutV2>{children}</BaseLayoutV2>
}

const MediaLibraryRouter = (): React.ReactElement => {
  const { path } = useRouteMatch()
  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const { user } = useContext(UserContext)
  const history = useHistory()
  const location = useLocation<BackgroundState>()

  // Detect if the REAL URL is a detail route (either ML or Collections)
  const isDetail = Boolean(
    matchPath(location.pathname, {
      path: DETAIL_PATHS,
      exact: true,
    }),
  )

  // To know if we're in collection detail (used for close behavior)
  const collectionDetailMatch =
    matchPath<{ mediaCollectionId: string }>(location.pathname, {
      path: [
        `/media-collections/:mediaCollectionId/detail/:fileId`,
        `/media-collections/:mediaCollectionId/detail/:fileId/:versionId`,
      ],
      exact: true,
    }) || null

  const [stableBg, setStableBg] = React.useState<Location | null>(null)

  // Maintain the first background when entering detail; otherwise track real location
  React.useEffect(() => {
    if (isDetail) {
      const bg = location.state?.background
      if (bg && !stableBg) {
        setStableBg(bg)
      }
    } else {
      setStableBg(location)
    }
  }, [isDetail, location]) // eslint-disable-line react-hooks/exhaustive-deps

  const closeDetail = React.useCallback(() => {
    if (stableBg) {
      history.goBack()
    } else if (collectionDetailMatch?.params?.mediaCollectionId) {
      // If we opened detail in a collection context directly, bounce back to that collection root
      history.replace(
        `/media-collections/${collectionDetailMatch.params.mediaCollectionId}`,
      )
    } else {
      history.replace('/media-library')
    }
  }, [stableBg, history, collectionDetailMatch])

  const rawSwitch = stableBg ?? location

  const switchLocation = React.useMemo(
    () => ({
      pathname: rawSwitch.pathname,
      search: rawSwitch.search,
      hash: rawSwitch.hash,
    }),
    [rawSwitch.pathname, rawSwitch.search, rawSwitch.hash],
  )

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null
    function waitForIntercom(): void {
      if ((window as any).Intercom?.booted) {
        try {
          ;(window as any).Intercom('hide')
          ;(window as any).Intercom('update', { hide_default_launcher: true })
        } catch {}
        return
      }
      timeoutId = setTimeout(waitForIntercom, 100)
    }
    waitForIntercom()
    return () => {
      try {
        ;(window as any).Intercom('update', { hide_default_launcher: false })
      } catch {}
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [])

  const canUseCollections =
    isPublicLink ||
    user?.pricingPlan.featuresList.includes(FeaturesEnum.ACL_AND_COLLECTIONS)

  // For public links, only allow detail routes without version ids
  const overlayPaths = isPublicLink
    ? [
        '/media-library/detail/:fileId',
        '/media-collections/:mediaCollectionId/detail/:fileId',
      ]
    : DETAIL_PATHS

  return (
    <CustomMetadataProvider>
      {/* Feed ML context from the BACKGROUND location */}
      <MediaLibraryProviderBridge sourceLocation={switchLocation}>
        {/* UNDERLAY */}
        <Switch location={switchLocation}>
          {/* Media Library routes */}
          <Route path={`${path}/generate-images`} exact>
            <BaseLayoutShell>
              <GenerateImagesPage />
            </BaseLayoutShell>
          </Route>

          <Route path={`/media-library/downloads`} exact>
            <DownloadsPage />
          </Route>

          <Route path={`/media-library/:folderPathBase64?`} exact>
            <BaseLayoutShell>
              <MediaLibraryPage />
            </BaseLayoutShell>
          </Route>

          {/* Search shares ML page */}
          <Route path="/search" exact>
            <BaseLayoutShell>
              <MediaLibraryPage />
            </BaseLayoutShell>
          </Route>

          {/* Media Collections routes now live here */}
          <Route
            path={`/media-collections/:mediaCollectionId/:folderPathBase64?`}
            exact
            render={() =>
              canUseCollections ? (
                <BaseLayoutShell>
                  <MediaCollectionsPage />
                </BaseLayoutShell>
              ) : (
                <BaseLayout>
                  <CustomPlanCTA feature={FeaturesEnum.ACL_AND_COLLECTIONS} />
                </BaseLayout>
              )
            }
          />

          {/* Collections root */}
          <Route
            path={`/media-collections/:mediaCollectionId?`}
            exact
            render={() =>
              canUseCollections ? (
                <BaseLayoutShell>
                  <MediaCollectionsPage />
                </BaseLayoutShell>
              ) : (
                <BaseLayout>
                  <CustomPlanCTA feature={FeaturesEnum.ACL_AND_COLLECTIONS} />
                </BaseLayout>
              )
            }
          />
        </Switch>

        <Route path={overlayPaths}>
          <FileDetailModal isOpen onClose={closeDetail} />
        </Route>
      </MediaLibraryProviderBridge>
    </CustomMetadataProvider>
  )
}

export default MediaLibraryRouter
